/**
 * Multi-suite membership chips for a single test, with an inline
 * "+ add to suite" picker.
 *
 * Each chip is removable (click the ×); the "+" opens a small
 * dropdown listing every suite this test is NOT already in.
 * Selecting a suite from that picker adds the test to it.
 *
 * **Save semantics:** every toggle (add OR remove) fires an
 * immediate `PUT /test-suites/:id` with the entire updated
 * `testCaseIds` array — that's the only mutation surface the
 * backend exposes today. Matches TestPlan.tsx's "immediate save
 * per chip toggle" pattern (its tags editor at line 256).
 * Last-write-wins is accepted; concurrent edits across two
 * browser tabs could race but the resulting state is still a
 * valid membership set.
 *
 * Loading: brief disable on the button while the request is in
 * flight. Errors surface via the `onError` callback (the parent
 * decides how to display).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Plus, X } from 'lucide-react'
import { Badge } from '@/components/ui'
import { testSuitesAPI } from '@/lib/api'
import type { TestSuite } from '@/types'

export interface MultiSuiteChipsProps {
  /** All suites in the current variant scope (so the picker can
   * surface suites this test isn't in yet). */
  allSuites: TestSuite[]
  /** Id of the test case whose memberships we're rendering. */
  testCaseId: string
  /** Called after a successful add/remove so the parent can
   * refetch + re-render. The parent owns the source of truth for
   * suite membership; this component only mutates the server. */
  onChanged: () => void | Promise<void>
  /** Surface server errors (e.g. permission denied, 5xx). The
   * parent renders an inline banner. */
  onError?: (message: string) => void
  /** Compact trigger for the header's row-2 strip (#2257): shows the
   * first suite name and a "+N" overflow, opening one popover that
   * holds the removable chips and the add-a-suite list. Same mutations
   * as the inline form; only the presentation differs. */
  compact?: boolean
}

export function MultiSuiteChips({
  allSuites,
  testCaseId,
  onChanged,
  onError,
  compact = false,
}: MultiSuiteChipsProps) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  // Click-outside dismiss for the picker. A `ref` + a document-
  // level pointerdown listener (registered only while the picker
  // is open) is the lightest approach without pulling in a
  // popover primitive. PR #543 Copilot review fix — the original
  // commit defined `pickerRef` and a "click-outside dismiss"
  // comment without actually wiring the listener.
  const pickerRef = useRef<HTMLDivElement>(null)
  // Compact popover: the trigger and the popover live in different DOM
  // subtrees (the popover is portaled to <body> so the row-2 strip can
  // scroll horizontally without clipping it — #2257 Copilot review), so
  // click-outside checks both.
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!pickerOpen) return
    function onDocClick(e: MouseEvent) {
      if (!pickerRef.current) return
      if (pickerRef.current.contains(e.target as Node)) return
      setPickerOpen(false)
    }
    // `pointerdown` fires before `click`, which means a click that
    // would have selected an option still works (the option's own
    // onClick runs first) — we just close on outside-pointer-down.
    document.addEventListener('pointerdown', onDocClick)
    return () => document.removeEventListener('pointerdown', onDocClick)
  }, [pickerOpen])
  useEffect(() => {
    if (!menuOpen) return
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node
      if (triggerRef.current?.contains(t)) return
      if (popRef.current?.contains(t)) return
      setMenuOpen(false)
    }
    document.addEventListener('pointerdown', onDocClick)
    return () => document.removeEventListener('pointerdown', onDocClick)
  }, [menuOpen])

  const memberOf = useMemo(
    () => allSuites.filter((s) => s.testCaseIds.includes(testCaseId)),
    [allSuites, testCaseId],
  )
  const notMemberOf = useMemo(
    () => allSuites.filter((s) => !s.testCaseIds.includes(testCaseId)),
    [allSuites, testCaseId],
  )

  // Partial-payload updates — only send the field we're changing.
  // The prior `...suite` spread could clobber concurrent header
  // edits (name / description) if a save-on-blur fired between the
  // chip click and the PUT. PR #543 Copilot review fix.
  async function addToSuite(suite: TestSuite) {
    if (busy) return
    setBusy(true)
    try {
      await testSuitesAPI.update(suite.id, {
        testCaseIds: [...suite.testCaseIds, testCaseId],
      })
      setPickerOpen(false)
      await onChanged()
    } catch (e) {
      onError?.(e instanceof Error ? e.message : 'Failed to add to suite')
    } finally {
      setBusy(false)
    }
  }

  async function removeFromSuite(suite: TestSuite) {
    if (busy) return
    setBusy(true)
    try {
      await testSuitesAPI.update(suite.id, {
        testCaseIds: suite.testCaseIds.filter((id) => id !== testCaseId),
      })
      await onChanged()
    } catch (e) {
      onError?.(e instanceof Error ? e.message : 'Failed to remove from suite')
    } finally {
      setBusy(false)
    }
  }

  if (compact) {
    // Anchor the portaled popover under the trigger. Read at render while
    // open; a click-menu doesn't move mid-open, so a single measure is
    // enough. Right-align to the trigger, clamped into the viewport.
    const rect = menuOpen ? triggerRef.current?.getBoundingClientRect() : undefined
    const POP_WIDTH = 256
    return (
      <div className="relative inline-block">
        <button
          ref={triggerRef}
          onClick={() => setMenuOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-muted whitespace-nowrap"
          title="Suites this test is in"
        >
          <span className="text-muted-foreground font-normal">Suites</span>
          {memberOf.length === 0 ? (
            <span className="text-muted-foreground">none</span>
          ) : (
            <>
              {/* Name truncates; the "+N" count keeps its own space so a
                  long first suite name can't hide how many more there are
                  (#2257 Copilot review). */}
              <span className="truncate max-w-[9rem]">{memberOf[0].name}</span>
              {memberOf.length > 1 && (
                <span className="text-muted-foreground shrink-0">+{memberOf.length - 1}</span>
              )}
            </>
          )}
          <ChevronDown className="h-3 w-3 shrink-0" />
        </button>
        {menuOpen && rect && createPortal(
          <div
            ref={popRef}
            style={{
              position: 'fixed',
              top: rect.bottom + 4,
              left: Math.max(8, rect.right - POP_WIDTH),
              width: POP_WIDTH,
            }}
            className="z-50 rounded-md border bg-popover shadow-lg p-2"
          >
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
              In suites
            </div>
            {memberOf.length === 0 ? (
              <div className="text-xs text-muted-foreground italic mb-1">Not in any suite yet</div>
            ) : (
              <div className="flex flex-wrap gap-1.5 mb-1">
                {memberOf.map((s) => (
                  <Badge key={s.id} variant="secondary" className="text-xs gap-1">
                    {s.name}
                    <button
                      onClick={() => removeFromSuite(s)}
                      disabled={busy}
                      className="hover:text-destructive disabled:opacity-50"
                      aria-label={`Remove from ${s.name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            {notMemberOf.length > 0 && (
              <>
                <div className="border-t my-1.5" />
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                  Add to a suite
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {notMemberOf.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => addToSuite(s)}
                      disabled={busy}
                      className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-muted/50 disabled:opacity-50 truncate"
                      title={s.name}
                    >
                      {s.name}
                      <span className="ml-2 text-[10px] text-muted-foreground">
                        {s.testCaseIds.length} tests
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>,
          document.body,
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mr-1">
        In suites
      </span>
      {memberOf.length === 0 && (
        <span className="text-xs text-muted-foreground italic">none</span>
      )}
      {memberOf.map((s) => (
        <Badge key={s.id} variant="secondary" className="text-xs gap-1">
          {s.name}
          <button
            onClick={() => removeFromSuite(s)}
            disabled={busy}
            className="hover:text-destructive disabled:opacity-50"
            aria-label={`Remove from ${s.name}`}
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      <div ref={pickerRef} className="relative">
        <button
          onClick={() => setPickerOpen((v) => !v)}
          disabled={busy || notMemberOf.length === 0}
          className="inline-flex items-center gap-0.5 px-2 py-0.5 text-xs rounded-full border border-dashed border-border text-muted-foreground hover:text-primary hover:border-primary disabled:opacity-50"
          title={notMemberOf.length === 0 ? 'Already in every suite' : 'Add to a suite'}
        >
          <Plus className="h-3 w-3" />
          {notMemberOf.length === 0 ? 'all suites' : 'add'}
        </button>
        {pickerOpen && notMemberOf.length > 0 && (
          <div className="absolute z-20 mt-1 w-56 max-h-64 overflow-y-auto rounded-md border bg-popover shadow-lg p-1">
            {notMemberOf.map((s) => (
              <button
                key={s.id}
                onClick={() => addToSuite(s)}
                disabled={busy}
                className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-muted/50 disabled:opacity-50 truncate"
                title={s.name}
              >
                {s.name}
                <span className="ml-2 text-[10px] text-muted-foreground">
                  {s.testCaseIds.length} tests
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
